import { Box, Chip, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

import { Service } from "../../types/service.type";
import { ServiceCategory, ServiceSubCategory } from "../../enum/service.enum";
import { formatLocalizedTimeAgo } from "../../../../utils/formatDate";

const ServiceCardBody = ({ service }: { service: Service }) => {
  const { name, description, category, subCategory, expiryDate } = service;
  const { t, i18n } = useTranslation("goService", { keyPrefix: "serviceCard" });

  return (
    <Box sx={styles.root}>
      <Box sx={styles.container}>
        <Typography sx={styles.title}>{name}</Typography>
        {expiryDate && (
          <Typography sx={styles.expiryText}>
            {`${t("expiresIn")} ${formatLocalizedTimeAgo(expiryDate, i18n.language)}`}
          </Typography>
        )}
        <Typography sx={styles.description}>{description}</Typography>
      </Box>
      <Box sx={styles.chipContainer}>
        {category && <CategoryChip category={category} />}
        {subCategory && <CategoryChip category={subCategory} />}
      </Box>
    </Box>
  );
};

const CategoryChip = ({ category }: { category: ServiceCategory | ServiceSubCategory }) => {
  const { t: tCategory } = useTranslation("goService", { keyPrefix: "serviceCategory" });
  const { t: tSubCategory } = useTranslation("goService", { keyPrefix: "serviceSubCategory" });

  const isMainCategory = Object.values(ServiceCategory).includes(category as ServiceCategory);

  return <Chip label={isMainCategory ? tCategory(category) : tSubCategory(category)} sx={styles.categoryChip} />;
};

const styles = {
  root: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    padding: "0 16px",
  },
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    minHeight: "108px",
  },
  title: {
    color: "var(--Dark---1000, #111)",
    fontFamily: "Montserrat",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "24px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    display: "-webkit-box",
    WebkitLineClamp: 1,
    WebkitBoxOrient: "vertical",
  },
  description: {
    color: "var(--Dark---600, #707070)",
    fontFamily: "Montserrat",
    fontSize: "12px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "18px",
    lineClamp: 3,
    overflow: "hidden",
    textOverflow: "ellipsis",
    display: "-webkit-box",
    WebkitLineClamp: 3,
    WebkitBoxOrient: "vertical",
    minHeight: "54px",
  },
  expiryText: {
    fontFamily: "Montserrat",
    color: "var(--Dark---1000, #111)",
    fontSize: "12px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "18px",
    minHeight: "18px",
  },
  chipContainer: {
    display: "flex",
    gap: "8px",
    flexWrap: "wrap",
  },
  statsContainer: {
    display: "flex",
    gap: "33px",
  },
  statText: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    color: "var(--Dark600, #707070)",
    fontFamily: "Montserrat",
    fontSize: "12px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "18px",
  },
  categoryChip: {
    color: "var(--Brand-600, #008B07)",
    fontFamily: "Montserrat",
    fontSize: "12px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "18px",
    background: "var(--Green---25, #E7EFED)",
    height: "fit-content",
    padding: "2px 10px",
    maxWidth: "calc(50% - 4px)",
  },
};

export default ServiceCardBody;
